import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Gauge, InfoIcon } from 'lucide-react';
import { MetaApp } from '../types/meta.types';

interface EditRateLimitDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    app: MetaApp | null;
    onSubmit: (rps: number) => void;
    isLoading: boolean;
}

const MIN_RPS = 1;
const MAX_RPS = 200;
const DEFAULT_RPS = 100;

export default function EditRateLimitDialog({
    open,
    onOpenChange,
    app,
    onSubmit,
    isLoading,
}: EditRateLimitDialogProps) {
    const [rps, setRps] = useState<number>(DEFAULT_RPS);

    // Reseta o input quando abrir/trocar de app
    useEffect(() => {
        if (open && app) {
            setRps(app.requests_per_second ?? DEFAULT_RPS);
        }
    }, [open, app]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const clamped = Math.max(MIN_RPS, Math.min(MAX_RPS, rps || DEFAULT_RPS));
        onSubmit(clamped);
    };

    const isUnchanged = app && rps === (app.requests_per_second ?? DEFAULT_RPS);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl font-bold text-[#0f172a]">
                        <Gauge className="h-5 w-5" />
                        Limite de envios
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Quantas requisições por segundo cada página deste app pode disparar.
                        {app && (
                            <>
                                {' '}
                                App: <span className="font-medium text-gray-800">{app.app_name}</span>
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <Alert className="border-[#7dd3fc] bg-[#7dd3fc]/5">
                    <InfoIcon className="h-4 w-4 text-[#0f172a]" />
                    <AlertDescription className="text-sm text-gray-700">
                        <strong className="font-semibold">Como funciona:</strong> esse limite é por
                        <strong> página</strong>. Se este app envia para 10 páginas em paralelo
                        com {rps}/s, o throughput total é {rps * 10}/s. Mudanças propagam em até
                        5min (cache no Redis).
                    </AlertDescription>
                </Alert>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="rate_limit_input" className="text-gray-700 font-medium">
                            Envios por segundo, por página
                        </Label>
                        <Input
                            id="rate_limit_input"
                            type="number"
                            min={MIN_RPS}
                            max={MAX_RPS}
                            value={rps}
                            onChange={(e) =>
                                setRps(Number(e.target.value) || DEFAULT_RPS)
                            }
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono text-lg"
                            autoFocus
                        />
                        <p className="text-xs text-gray-500">
                            Mínimo: {MIN_RPS} · Padrão: {DEFAULT_RPS} · Máximo permitido pela Meta: {MAX_RPS}
                        </p>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                            className="border-gray-300"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || !!isUnchanged}
                            className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                        >
                            {isLoading ? (
                                <>
                                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
